import humps from 'humps'

import { create, fetch } from 'app/utils/data'
import { default as deserialize } from 'app/utils/deserialize'

// EnforcePolicy refuses with a plain-text 403 rather than a JSON:API envelope,
// so a response body is not always JSON. Everything else on these endpoints is
// an envelope.
function failureFrom(error, fallback) {
  const response = error && error.response

  if (!response) {
    return { message: fallback, field: null }
  }

  const errors = response.data && response.data.errors

  if (Array.isArray(errors) && errors.length && errors[0].detail) {
    return { message: errors[0].detail, field: fieldFrom(errors[0]) }
  }

  if (403 === response.status) {
    return {
      message: 'Your role on this account cannot accept a license. An owner, admin, or billing contact can.',
      field: null
    }
  }

  return { message: fallback, field: null }
}

// A changeset error carries the attribute it came from, so the form can put it
// under the field that caused it instead of in a banner away from the input.
function fieldFrom(jsonApiError) {
  const pointer = jsonApiError.source && jsonApiError.source.pointer

  return pointer ? humps.camelize(pointer.split('/').pop()) : null
}

export default {
  namespaced: true,

  state: {
    bands: [],
    terms: null
  },

  getters: {
    bands: (state) => state.bands,
    terms: (state) => state.terms
  },

  mutations: {
    setBands(state, bands) {
      state.bands = bands
    },

    setTerms(state, terms) {
      state.terms = terms
    }
  },

  actions: {
    async fetchBands({ commit }) {
      const response = await fetch({ url: '/api/platform-license/bands' })
      const bands = await deserialize(response.data)

      commit('setBands', bands)
    },

    async fetchTerms({ commit }) {
      const response = await fetch({ url: '/api/platform-license/addendum' })
      const terms = await deserialize(response.data)

      commit('setTerms', terms)
    },

    // The account's pending or active license, or null. A refusal (no paid
    // plan, or a role that cannot accept) means there is none to show; any
    // other failure is rethrown, so a licensee is never shown a blank form.
    async fetchCurrentAcceptance() {
      try {
        const response = await fetch({ url: '/api/platform-license/acceptances/current' })

        return response.data.data ? await deserialize(response.data) : null
      } catch (error) {
        const status = (error?.response ?? error)?.status

        if (401 === status || 403 === status) {
          return null
        }

        throw error
      }
    },

    async submitAcceptance(_context, attributes) {
      try {
        const response = await create({
          url: '/api/platform-license/acceptances',
          type: 'platform-license-acceptance',
          data: attributes
        })

        return await deserialize(response.data)
      } catch (error) {
        const failure = failureFrom(error, 'We could not record that. Try again, or get in touch.')
        const thrown = new Error(failure.message)

        thrown.field = failure.field

        throw thrown
      }
    }
  }
}
